import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  IconButton,
  Typography,
  CircularProgress,
  Stack,
  Alert,
  Checkbox,
} from '@mui/material';
import { Delete, CloudUpload, PhotoLibrary } from '@mui/icons-material';
import DialogModal from 'components/UI/DialogModal';
import MuiButton from 'components/UI/MuiButton';
import { useAlert } from 'contexts/alertContext';
import { WorkshopPhoto } from 'models/WorkshopPhotos';
import { handleError } from 'helpers/utils';
import {
  getWorkshopPhotos,
  uploadWorkshopPhotos,
  deleteWorkshopPhoto,
} from 'framework/workshopPhotos';

interface Props {
  open: boolean;
  onClose: () => void;
  workshopId: string;
}

const WorkshopPhotosModal: React.FC<Props> = ({ open, onClose, workshopId }) => {
  const [photos, setPhotos] = useState<WorkshopPhoto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [selectedPhotos, setSelectedPhotos] = useState<Set<string>>(new Set());
  const { setAlert } = useAlert();

  const fetchPhotos = async () => {
    try {
      setLoading(true);
      const photos = await getWorkshopPhotos(workshopId);
      setPhotos(photos);
      setSelectedPhotos(new Set()); // Clear selections on fetch
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || 'Failed to load photos',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && workshopId) {
      fetchPhotos();
    }
    // eslint-disable-next-line
  }, [open, workshopId]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploading(true);
      const filesArray = Array.from(files);
      const response = await uploadWorkshopPhotos(workshopId, filesArray);

      setAlert({
        open: true,
        message: response.message || 'Photos uploaded successfully',
        type: 'success',
      });

      // Refresh photos list
      fetchPhotos();
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || 'Failed to upload photos',
        type: 'error',
      });
    } finally {
      setUploading(false);
      event.target.value = ''; // Reset input
    }
  };

  const handleDelete = async (photoId: string, imageName: string) => {
    if (!window.confirm(`Are you sure you want to delete ${imageName}?`)) return;

    try {
      const response = await deleteWorkshopPhoto(photoId);
      
      setAlert({
        open: true,
        message: response.message || 'Photo deleted successfully',
        type: 'success',
      });

      // Refresh photos list
      fetchPhotos();
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || 'Failed to delete photo',
        type: 'error',
      });
    }
  };

  const handleToggleSelect = (photoId: string) => {
    const newSelected = new Set(selectedPhotos);
    if (newSelected.has(photoId)) {
      newSelected.delete(photoId);
    } else {
      newSelected.add(photoId);
    }
    setSelectedPhotos(newSelected);
  };

  const handleDeleteSelected = async () => {
    if (selectedPhotos.size === 0) return;
    
    const photoNames = photos
      .filter((photo) => selectedPhotos.has(photo.id!))
      .map((photo) => photo.imageName)
      .join(', ');
    
    if (!window.confirm(`Are you sure you want to delete ${selectedPhotos.size} photo(s): ${photoNames}?`)) return;

    try {
      // Delete all selected photos
      const deletePromises = Array.from(selectedPhotos).map((photoId) =>
        deleteWorkshopPhoto(photoId)
      );
      
      await Promise.all(deletePromises);
      
      setAlert({
        open: true,
        message: `${selectedPhotos.size} photo(s) deleted successfully`,
        type: 'success',
      });

      // Refresh photos list
      fetchPhotos();
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || 'Failed to delete photos',
        type: 'error',
      });
    }
  };

  const handleSelectAll = () => {
    const allPhotoIds = new Set(photos.map(photo => photo.id!));
    
    // If all are selected, deselect all; otherwise select all
    if (selectedPhotos.size === photos.length) {
      setSelectedPhotos(new Set());
    } else {
      setSelectedPhotos(allPhotoIds);
    }
  };

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title="Workshop Photos"
      maxWidth="md"
      fullWidth
    >
      <Box sx={{ minHeight: 300, p: 2 }}>
        {/* Upload Section */}
        <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="photo-upload-button"
            multiple
            type="file"
            onChange={handleFileSelect}
            disabled={uploading}
          />
          <label htmlFor="photo-upload-button">
            <MuiButton
              variant="contained"
              component="span"
              startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Photos'}
            </MuiButton>
          </label>
          
          {!loading && photos.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={selectedPhotos.size > 0 && selectedPhotos.size === photos.length}
                indeterminate={selectedPhotos.size > 0 && selectedPhotos.size < photos.length}
                onChange={handleSelectAll}
                sx={{
                  color: '#1976d2',
                  '&.Mui-checked': {
                    color: '#1976d2',
                  },
                }}
              />
              <Typography variant="body2" color="textSecondary">
                {selectedPhotos.size > 0 
                  ? `${selectedPhotos.size} of ${photos.length} selected`
                  : `Select all (${photos.length})`}
              </Typography>
            </Box>
          )}
          
          {selectedPhotos.size > 0 && (
            <MuiButton
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={handleDeleteSelected}
              sx={{ ml: 'auto' }}
            >
              Delete Selected ({selectedPhotos.size})
            </MuiButton>
          )}
        </Stack>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Empty State */}
        {!loading && photos.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
              border: '2px dashed #ccc',
              borderRadius: 2,
            }}
          >
            <PhotoLibrary sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="textSecondary">
              No photos uploaded yet
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Click "Upload Photos" to add images
            </Typography>
          </Box>
        )}

        {/* Photos Grid */}
        {!loading && photos.length > 0 && (
          <Grid container spacing={2}>
            {photos.map((photo) => (
              <Grid item xs={12} sm={6} md={4} key={photo.id}>
                <Box
                  sx={{
                    position: 'relative',
                    paddingTop: '100%', // 1:1 Aspect Ratio
                    borderRadius: 1,
                    overflow: 'hidden',
                    boxShadow: 2,
                    cursor: 'pointer',
                    '&:hover .delete-button, &:hover .checkbox-wrapper': {
                      opacity: 1,
                    },
                    border: selectedPhotos.has(photo.id!) ? '3px solid #1976d2' : 'none',
                  }}
                  onClick={() => handleToggleSelect(photo.id!)}
                >
                  <Box
                    component="img"
                    src={photo.imageUrl}
                    alt={photo.imageName}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    onError={(e: any) => {
                      e.target.src = 'https://via.placeholder.com/300?text=Image+Not+Found';
                    }}
                  />
                  <Box
                    className="checkbox-wrapper"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      borderRadius: 1,
                      opacity: selectedPhotos.has(photo.id!) ? 1 : 0,
                      transition: 'opacity 0.2s',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleSelect(photo.id!);
                    }}
                  >
                    <Checkbox
                      checked={selectedPhotos.has(photo.id!)}
                      sx={{
                        p: 0.5,
                        color: '#1976d2',
                        '&.Mui-checked': {
                          color: '#1976d2',
                        },
                      }}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleToggleSelect(photo.id!);
                      }}
                    />
                  </Box>
                  <IconButton
                    className="delete-button"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      opacity: 0,
                      transition: 'opacity 0.2s',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 0, 0, 0.8)',
                        color: 'white',
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(photo.id!, photo.imageName);
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      p: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                      }}
                    >
                      {photo.imageName}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </DialogModal>
  );
};

export default WorkshopPhotosModal;

